import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { formsApi } from '@/lib/api/forms';
import { FormSubmissionsPanel } from '@/components/shared/form-submissions-panel';
import type { Form, ResourceOperation } from '@/types/entities';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Card } from '@/components/ui/card';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

export function FormSubmissionsPage() {
  const { user } = useAuth();
  const { endpoint, submission_id } = useParams<{ endpoint: string; submission_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<Form | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const editorMode = location.pathname.endsWith('/create')
    ? 'create'
    : location.pathname.endsWith('/edit')
      ? 'edit'
      : null;

  const editingSubmissionId = submission_id ? Number(submission_id) : null;

  const allowedOps = form?.details.ops?.length
    ? Array.from(new Set<ResourceOperation>(form.details.ops))
    : ALL_OPERATIONS;

  useEffect(() => {
    const fetchForm = async () => {
      if (!user?.tenant || !endpoint) return;

      setIsLoading(true);
      setError(null);
      try {
        // Find the form by endpoint from the tenant's forms
        const formList = user.tenant.forms;
        const foundForm = formList.find(f => f.endpoint === endpoint);

        if (!foundForm) {
          setError('Form not found');
          return;
        }

        // Get the full form details with parsed schema
        const fullForm = await formsApi.getById(user.tenant.slug, foundForm.id);
        setForm(fullForm as Form);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load form');
      } finally {
        setIsLoading(false);
      }
    };

    fetchForm();
  }, [user?.tenant, endpoint]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading form...</div>
      </div>
    );
  }

  if (error || !form || !user?.tenant) {
    return (
      <Card className="p-4">
        <div className="text-destructive">{error || 'Form not found'}</div>
      </Card>
    );
  }

  if (editorMode === 'create' && !allowedOps.includes('create')) {
    return (
      <Card className="p-4">
        <div className="text-destructive">Create is disabled for this form</div>
      </Card>
    );
  }

  if (editorMode === 'edit' && !allowedOps.includes('update')) {
    return (
      <Card className="p-4">
        <div className="text-destructive">Update is disabled for this form</div>
      </Card>
    );
  }

  return (
    <>
      {!editorMode && (
        <PageHeader
          title={form.display_name}
          description={form.details.enabled !== false ? 'Manage form submissions' : 'Manage form submissions (public submit is disabled)'}
        />
      )}
      <PageSection>
        {user.tenant && (
          <FormSubmissionsPanel
            tenant={{
              id: user.tenant.id,
              slug: user.tenant.slug,
              name: user.tenant.name,
              websiteUrl: user.tenant.websiteUrl,
            }}
            form={form}
            enforceAccess
            editorMode={editorMode}
            editingSubmissionId={editingSubmissionId}
            onCreateRoute={() => navigate(`/tenant/dashboard/forms/${form.endpoint}/create`)}
            onEditRoute={(itemId) => navigate(`/tenant/dashboard/forms/${form.endpoint}/${itemId}/edit`)}
            onExitEditorRoute={() => navigate(`/tenant/dashboard/forms/${form.endpoint}`)}
          />
        )}
      </PageSection>
    </>
  );
}
